import React, {useCallback} from "react";
import {Formik, FormikProps} from "formik";
import {Button, TextField, Subtitle, Card, SelectField} from "@reside/ui";
import {PccFacilityMappingDto} from "@reside/reside-api-app";
import {DocumentUploadCategory} from "@reside/reside-api-admission";

import {BackdropSpinner} from "../../../../atoms/spinner";
import {
  usePccFacilityMappingQuery,
  usePccFacilityMappingMutation,
} from "../../hooks";
import {useFacilitySettingsParams} from "../../route";
import {useDocumentUploadCategoriesQuery} from "../../../../models/pcc";
import {Footer} from "./styles";

const INITIAL_VALUES: PccFacilityMappingDto = {
  pccFacilityId: "" as unknown as number,
  pccOrganizationId: "",
};

export const FacilityPccMappingForm = ({
  isPccEnabled,
}: {
  isPccEnabled: boolean;
}) => {
  const {facilityId} = useFacilitySettingsParams();

  const {data: pccFacilityMapping, isFetching} = usePccFacilityMappingQuery({
    facilityId,
  });

  const {mutate: updatePccFacilityMapping, isLoading} =
    usePccFacilityMappingMutation();
  const {
    data: documentUploadCategories = [],
    isLoading: isLoadingPccCategories,
  } = useDocumentUploadCategoriesQuery({
    facilityId,
    enabled: isPccEnabled,
  });

  const onSave = useCallback(
    async (pccFacilityMapping: PccFacilityMappingDto) => {
      updatePccFacilityMapping({
        facilityId,
        pccFacilityMapping,
      });
    },
    [updatePccFacilityMapping, facilityId],
  );

  /**
   * At the moment of writing this formik did not have ability to check dirty state of individual fields. This is a workaround: https://github.com/jaredpalmer/formik/issues/3612
   */
  function isInputDirty(
    name: keyof PccFacilityMappingDto,
    formik: FormikProps<PccFacilityMappingDto>,
  ) {
    const boolean =
      // Avoid strict equality: initial value is number, but becomes string on change.
      // eslint-disable-next-line eqeqeq
      formik.getFieldMeta(name).value == formik.getFieldMeta(name).initialValue;

    return !boolean;
  }

  return (
    <BackdropSpinner active={isFetching || isLoadingPccCategories}>
      <Formik<PccFacilityMappingDto>
        enableReinitialize
        initialValues={
          Object.keys(pccFacilityMapping ?? {}).length
            ? pccFacilityMapping
            : INITIAL_VALUES
        }
        onSubmit={onSave}
      >
        {formik => (
          <Card>
            <Subtitle>PointClickCare Connection Settings</Subtitle>
            <TextField
              label="PointClickCare Organization UUID"
              name="pccOrganizationId"
            />
            <TextField
              label="PointClickCare Facility ID"
              name="pccFacilityId"
              format="integer"
            />
            {isPccEnabled && (
              <SelectField<DocumentUploadCategory>
                name="defaultDocumentCategoryIdentifier"
                label={
                  isLoadingPccCategories
                    ? "Loading, PointClickCare Document Categories"
                    : "PointClickCare Document Category for final PDFs (Choose from list)"
                }
                disabled={isLoadingPccCategories}
                options={documentUploadCategories}
                getOptionValue={category => category.id}
                getOptionLabel={category => category.name}
              />
            )}
            <Footer>
              <Button
                color="success"
                testId="pccSettingsBtn"
                disabled={
                  (!isInputDirty("pccFacilityId", formik) &&
                    !isInputDirty("pccOrganizationId", formik)) ||
                  isLoading
                }
                onClick={formik.submitForm}
              >
                Save
              </Button>
              <Button
                color="primary"
                outline
                testId="pccSettingsBtn"
                disabled={
                  isLoading ||
                  (!formik.values.pccFacilityId &&
                    !formik.values.pccOrganizationId)
                }
                onClick={() => {
                  formik.setFieldValue("pccOrganizationId", "");

                  formik.setFieldValue(
                    "pccFacilityId",
                    "" as unknown as number,
                  );
                }}
              >
                Clear PCC ID fields
              </Button>
            </Footer>
          </Card>
        )}
      </Formik>
    </BackdropSpinner>
  );
};
